import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Timer, Truck, PersonSimpleWalk, Warning, Stack, Printer, Copy, Brain,
  Check, X as XIcon, ArrowRight, ArrowDown, Sparkle, Gauge, Leaf,
  MapPinLine, Receipt, ChatsCircle, FileX, Money, Clock, GasPump,
  CalendarCheck, ChartLineUp, Smiley, ShieldCheck, Cube,
} from "@phosphor-icons/react";

import { SEO } from "@/components/SEO";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { Eyebrow, SectionHeading, BookDemoButton, LinkButton } from "@/components/marketing/Primitives";
import { LeanCalculator } from "@/components/marketing/LeanCalculator";

const HERO_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920";

/* ---------------- Data ---------------- */
const wastes = [
  { n: "01", icon: Timer, title: "Waiting", ex: ["Waiting for paperwork", "Waiting for approvals", "Waiting for engineers", "Waiting for stock"], fix: ["Instant updates", "Digital workflows", "Real-time communication"] },
  { n: "02", icon: Truck, title: "Transport", ex: ["Engineers travelling unnecessarily", "Extra journeys", "Wrong engineer assigned"], fix: ["Smart scheduling", "Live engineer tracking", "Job planning"] },
  { n: "03", icon: PersonSimpleWalk, title: "Motion", ex: ["Searching for keys", "Searching for manuals", "Customer history", "Photographs"], fix: ["Everything stored digitally"] },
  { n: "04", icon: Warning, title: "Defects", ex: ["Repeat visits", "Incorrect paperwork", "Incorrect parts ordered"], fix: ["Digital forms", "Required checklists", "Mandatory photographs", "Engineer guidance"] },
  { n: "05", icon: Stack, title: "Inventory", ex: ["Too much stock", "Missing stock", "Duplicate purchases"], fix: ["Vehicle stock", "Warehouse stock", "Engineer stock", "QR codes & asset management"] },
  { n: "06", icon: Printer, title: "Overproduction", ex: ["Printing paperwork", "Duplicate reports", "Repeated admin"], fix: ["A single digital workflow"] },
  { n: "07", icon: Copy, title: "Overprocessing", ex: ["Entering information twice", "Copying emails", "Manual spreadsheets"], fix: ["Information entered once", "Automatically shared across the platform"] },
  { n: "08", icon: Brain, title: "Unused Talent", ex: ["Engineers wasting time", "Managers chasing paperwork", "Office staff on repetitive admin"], fix: ["Automation", "AI", "Digital workflows", "Live reporting"] },
];

const hubNodes = ["Scheduling", "Dispatch", "Jobs", "Assets", "Customers", "Stock", "Photos", "Documents", "AI", "Messaging", "Reporting", "Mobile App"];

const lossStats = [
  { icon: GasPump, value: 4200, prefix: "£", suffix: "", label: "Avg. wasted per engineer / year on unnecessary travel" },
  { icon: Receipt, value: 30, suffix: "%", label: "Admin time lost to duplicate data entry" },
  { icon: ArrowRight, value: 1, suffix: " in 5", label: "Jobs needing a repeat visit without digital guidance" },
  { icon: Money, value: 12, suffix: " days", label: "Average delay from job complete to invoice sent" },
];

const lossList = [
  "Engineers driving unnecessarily", "Duplicate admin", "Repeat visits", "Missing stock",
  "Late invoicing", "Poor communication", "Lost paperwork", "Waiting for approvals",
];

const beforeItems = ["Paper jobs", "WhatsApp", "Emails", "Excel", "Multiple systems", "Lost documents", "Office phone calls", "Unknown engineer location", "Repeated admin"];
const afterItems = ["Everything connected", "Mobile app", "Digital forms", "Live updates", "Customer portal", "Asset history", "AI insights", "Automated reporting", "Stock management", "Real-time dashboard"];

const benefits = [
  { icon: FileX, t: "Less paperwork" }, { icon: MapPinLine, t: "Less travel" },
  { icon: Receipt, t: "Faster invoicing" }, { icon: Gauge, t: "Higher productivity" },
  { icon: ChatsCircle, t: "Better communication" }, { icon: GasPump, t: "Reduced fuel costs" },
  { icon: CalendarCheck, t: "Improved scheduling" }, { icon: Copy, t: "Reduced administration" },
  { icon: ChartLineUp, t: "More completed jobs" }, { icon: Money, t: "Higher profit" },
  { icon: Smiley, t: "Better customer experience" }, { icon: ShieldCheck, t: "Greater compliance" },
];

const aiPoints = [
  "Automatic reports", "Job summaries", "Customer history", "Predictive maintenance",
  "Engineer assistance", "Suggested stock", "Automatic reminders", "Automatic scheduling",
];

const compareRows = [
  { label: "Designed by field engineers", fc: true, other: false },
  { label: "Built around Lean principles", fc: true, other: false },
  { label: "AI integrated", fc: true, other: false },
  { label: "Fully connected platform", fc: true, other: false },
  { label: "Future ready", fc: true, other: false },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ---------------- Infographic ---------------- */
function Infographic() {
  return (
    <div className="relative mx-auto mt-14 max-w-4xl">
      {/* Desktop orbit */}
      <div className="relative mx-auto hidden aspect-square max-w-[560px] lg:block">
        <div className="absolute left-1/2 top-1/2 h-[62%] w-[62%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/10" />
        <div className="absolute left-1/2 top-1/2 h-[100%] w-[100%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/5" />
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="absolute left-1/2 top-1/2 flex h-28 w-28 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-primary text-center text-primary-foreground glow-green"
        >
          <span className="font-heading text-lg font-extrabold leading-none">FlowCore+</span>
          <span className="mt-1 text-[10px] font-semibold opacity-80">One core</span>
        </motion.div>
        {hubNodes.map((node, i) => {
          const angle = (i / hubNodes.length) * Math.PI * 2 - Math.PI / 2;
          const R = 45; // percent radius
          const left = 50 + R * Math.cos(angle);
          const top = 50 + R * Math.sin(angle);
          return (
            <motion.div
              key={node}
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 + i * 0.05, duration: 0.4 }}
              className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-white/10 bg-card px-3.5 py-1.5 text-xs font-semibold text-white shadow-lg"
              style={{ left: `${left}%`, top: `${top}%` }}
            >
              {node}
            </motion.div>
          );
        })}
      </div>

      {/* Mobile grid */}
      <div className="lg:hidden">
        <div className="mx-auto mb-6 flex h-24 w-24 flex-col items-center justify-center rounded-full bg-primary text-center text-primary-foreground glow-green">
          <span className="font-heading text-base font-extrabold">FlowCore+</span>
          <span className="text-[9px] font-semibold opacity-80">One core</span>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {hubNodes.map((n) => (
            <span key={n} className="rounded-full border border-white/10 bg-card px-3 py-2 text-center text-xs font-semibold text-white">{n}</span>
          ))}
        </div>
      </div>

      <p className="mt-10 text-center text-sm text-muted-foreground">
        Everything is connected to one core — so there's no duplicated work, no lost data and no gaps between the field and the office.
      </p>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function LeanOperations() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Lean Operations for Field Service — FlowCore+",
    description: "FlowCore+ is a Lean Operations platform for field service businesses — reduce waste, increase productivity and improve profits.",
  };

  return (
    <>
      <SEO
        title="Lean Operations Software for Field Service | FlowCore+"
        description="FlowCore+ is built on Lean principles to eliminate waste in field service businesses — reduce engineer downtime, cut travel and admin, and boost productivity and profit. Lean field service software UK."
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Field engineers and managers using FlowCore+" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        </div>
        <div className="pointer-events-none absolute inset-0 radial-fade" />
        <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <Eyebrow className="mx-auto">Lean Operations Platform</Eyebrow>
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Lean Operations for <span className="text-primary">Field Service</span> Businesses
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Reduce waste. Increase productivity. Improve profits.<br />
              <span className="text-white/80">Built by field engineers and managers.</span>
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <BookDemoButton testid="lean-hero-demo" />
              <button
                onClick={() => scrollTo("what-is-lean")}
                data-testid="lean-hero-learn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.02] px-6 py-3 text-sm font-semibold text-white transition-all hover:scale-[1.02] hover:border-primary/60"
              >
                Learn More <ArrowDown size={16} weight="bold" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 1 — What is Lean */}
      <section id="what-is-lean" className="py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <SectionHeading eyebrow="The principle" title="What is Lean Management?" />
            <div className="mt-6 space-y-4 text-muted-foreground md:text-lg">
              <p>Lean Management is about continuously improving the way a business works by removing activities that do not add value.</p>
              <p>Rather than asking staff to work harder, Lean focuses on helping teams work <span className="text-white">smarter</span>.</p>
              <p>FlowCore+ has been designed around these principles to help businesses eliminate wasted time, unnecessary travel, duplicated work and inefficient processes.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-card p-8">
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/15 blur-3xl" />
              <div className="grid grid-cols-2 gap-4">
                {[{ i: Leaf, t: "Add value" }, { i: Timer, t: "Remove waiting" }, { i: Gauge, t: "Work smarter" }, { i: ChartLineUp, t: "Improve daily" }].map((x) => {
                  const Icon = x.i;
                  return (
                    <div key={x.t} className="rounded-2xl border border-white/10 bg-background p-5">
                      <Icon size={26} weight="duotone" className="text-primary" />
                      <p className="mt-3 text-sm font-semibold text-white">{x.t}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 2 — 8 Wastes */}
      <section className="bg-card/30 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="Eliminate waste" title="The 8 Types of Waste" sub="Lean identifies eight sources of waste in any operation. FlowCore+ is built to remove each one." />
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" stagger={0.05}>
            {wastes.map((w) => {
              const Icon = w.icon;
              return (
                <StaggerItem key={w.title}>
                  <div className="group flex h-full flex-col rounded-2xl border border-white/10 bg-background p-6 transition-colors hover:border-primary/40">
                    <div className="flex items-center justify-between">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-110">
                        <Icon size={22} weight="duotone" />
                      </span>
                      <span className="font-heading text-sm font-bold text-white/20">{w.n}</span>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-white">{w.title}</h3>
                    <ul className="mt-3 space-y-1.5">
                      {w.ex.map((e) => (
                        <li key={e} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <XIcon size={12} weight="bold" className="mt-0.5 flex-none text-white/30" /> {e}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 rounded-xl bg-primary/[0.07] p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-primary">FlowCore+ solution</p>
                      <ul className="mt-1.5 space-y-1">
                        {w.fix.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-xs text-white">
                            <Check size={12} weight="bold" className="mt-0.5 flex-none text-primary" /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* SECTION 3 — Infographic */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="One connected platform" title="How FlowCore+ Reduces Waste" sub="Disconnected tools create waste at every hand-off. FlowCore+ connects everything to a single core." />
          </Reveal>
          <Reveal delay={0.1}><Infographic /></Reveal>
        </div>
      </section>

      {/* SECTION 4 — Where businesses lose money */}
      <section className="bg-card/30 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="The cost of waste" title="Where Businesses Lose Money" sub="Small inefficiencies compound into thousands lost every year. FlowCore+ reduces each of these." />
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {lossStats.map((s) => {
              const Icon = s.icon;
              return (
                <StaggerItem key={s.label}>
                  <div className="h-full rounded-2xl border border-white/10 bg-background p-6 text-center transition-colors hover:border-primary/40">
                    <Icon size={28} weight="duotone" className="mx-auto text-primary" />
                    <p className="mt-3 font-heading text-3xl font-extrabold text-white sm:text-4xl">
                      <Counter value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">{s.label}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            {lossList.map((l) => (
              <span key={l} className="rounded-full border border-white/10 bg-card px-3.5 py-1.5 text-xs font-medium text-muted-foreground">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Lean Calculator */}
      <LeanCalculator />

      {/* SECTION 5 — Before / After */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="The transformation" title="Before &amp; After FlowCore+" />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-white/10 bg-card/60 p-8">
                <h3 className="font-heading text-xl font-semibold text-muted-foreground">Traditional Business</h3>
                <ul className="mt-6 space-y-3">
                  {beforeItems.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-muted-foreground">
                      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white/5 text-white/40"><XIcon size={13} weight="bold" /></span>
                      <span className="line-through decoration-white/20">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative h-full overflow-hidden rounded-3xl border border-primary/40 bg-card p-8 glow-green">
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
                <h3 className="font-heading text-xl font-semibold text-white">FlowCore+</h3>
                <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {afterItems.map((a) => (
                    <li key={a} className="flex items-center gap-3 text-white">
                      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/20 text-primary"><Check size={13} weight="bold" /></span>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 6 — Benefits */}
      <section className="bg-card/30 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="The payoff" title="Lean Benefits" sub="What a leaner operation actually delivers for your business." />
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" stagger={0.04}>
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <StaggerItem key={b.t}>
                  <div className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-background p-5 transition-all hover:-translate-y-1 hover:border-primary/40">
                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:rotate-6">
                      <Icon size={20} weight="duotone" />
                    </span>
                    <span className="text-sm font-medium text-white">{b.t}</span>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* SECTION 7 — AI + Lean */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>AI + Lean</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">AI that removes waste automatically</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground md:text-lg">
              FlowCore+ uses AI to take repetitive, low-value work off your team — turning raw job data into insight and action, without extra admin.
            </p>
            <div className="mt-6 rounded-2xl border border-white/10 bg-card p-5 text-sm text-muted-foreground">
              <span className="font-semibold text-primary">Future ready:</span> new AI capabilities are added continuously, so your operation keeps getting leaner over time.
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Stagger className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {aiPoints.map((p) => (
                <StaggerItem key={p}>
                  <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-background p-4">
                    <Sparkle size={18} weight="fill" className="flex-none text-primary" />
                    <span className="text-sm font-medium text-white">{p}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </Reveal>
        </div>
      </section>

      {/* SECTION 8 — Why different */}
      <section className="bg-card/30 py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="The difference" title="Why FlowCore+ is Different" />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 overflow-hidden rounded-3xl border border-white/10">
              <div className="grid grid-cols-[1fr_120px_120px] bg-card">
                <div className="p-4 text-sm font-semibold text-muted-foreground">Capability</div>
                <div className="p-4 text-center text-sm font-semibold text-primary">FlowCore+</div>
                <div className="p-4 text-center text-sm font-semibold text-white">Other FSM</div>
              </div>
              {compareRows.map((r, i) => (
                <div key={r.label} className={`grid grid-cols-[1fr_120px_120px] items-center ${i % 2 ? "bg-card/40" : "bg-transparent"}`}>
                  <div className="p-4 text-sm text-white">{r.label}</div>
                  <div className="p-4 text-center"><Check size={18} weight="bold" className="mx-auto text-primary" /></div>
                  <div className="p-4 text-center"><XIcon size={16} weight="bold" className="mx-auto text-white/25" /></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 9 — Continuous improvement */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <Eyebrow className="mx-auto">Continuous improvement</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">More than software — your operating system</h2>
            <div className="mt-6 space-y-4 text-muted-foreground md:text-lg">
              <p>FlowCore+ is not simply software. It becomes the operating system for your business.</p>
              <p>Every job creates data. Every report provides insight. Every engineer contributes to improving the company.</p>
              <p className="text-white">The more FlowCore+ is used, the smarter and more efficient the business becomes.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 pb-24 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] border border-primary/40 bg-primary/[0.06] p-10 text-center sm:p-16 glow-green">
              <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
              <h2 className="relative font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Ready to remove waste from your business?
              </h2>
              <p className="relative mx-auto mt-4 max-w-xl text-muted-foreground md:text-lg">
                Book a demonstration today and see how much leaner your operation could be.
              </p>
              <div className="relative mt-8 flex flex-wrap items-center justify-center gap-4">
                <BookDemoButton label="Book My Demo" testid="lean-cta-demo" className="px-8 py-3.5 text-base" />
                <LinkButton to="/pricing" testid="lean-cta-pricing">View Pricing <ArrowRight size={16} weight="bold" /></LinkButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
